import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import { getAuth } from 'firebase/auth'
import { _ENV_VARIABLES } from '@/utils/constants'
import { logger } from '@/utils/logger'

// Marks a request that was already resent after a 401, so a second 401 rejects instead of looping
interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  __isRetry?: boolean
}

// Create axios instance with base URL
const apiClient: AxiosInstance = axios.create({
  baseURL: _ENV_VARIABLES.BACKEND_URL_PROD,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Request interceptor - add Firebase token to all requests
apiClient.interceptors.request.use(
  async config => {
    const currentUser = getAuth().currentUser
    if (currentUser) {
      try {
        // Cached read: Firebase renews the token before expiry, and a 401 force-refreshes it below
        const token = await currentUser.getIdToken()
        config.headers.Authorization = `Bearer ${token}`
      } catch (error) {
        logger.error('Failed to get auth token', error as Error, {
          component: 'api'
        })
      }
    }
    return config
  },
  error => Promise.reject(error)
)

// Response interceptor - handle 401 errors
apiClient.interceptors.response.use(
  response => response,
  async error => {
    const config: RetryableRequestConfig | undefined = error.config
    if (error.response?.status === 401 && config && !config.__isRetry) {
      const currentUser = getAuth().currentUser
      if (currentUser) {
        try {
          const newToken = await currentUser.getIdToken(true)
          config.__isRetry = true
          config.headers.Authorization = `Bearer ${newToken}`
          return apiClient.request(config)
        } catch (tokenError) {
          logger.error('Token refresh failed', tokenError as Error, {
            component: 'api'
          })
        }
      }
    }
    return Promise.reject(error)
  }
)

/**
 * Load API functions
 */
export const loadApi = {
  /**
   * Search for loads using searchQuery
   * This searches across: load number, customer load number, customer/broker name
   * Uses the same searchQuery pattern as the main B18 app
   */
  searchLoads: async (query: string) => {
    const params = new URLSearchParams()
    // searchQuery searches across multiple fields: loadNumber, customer.loadNumber, customer.name, broker.name
    params.append('conditions[searchQuery]', query.trim())
    params.append('options[limit]', '5')
    params.append('options[sort][loadNumber]', 'desc')

    const response = await apiClient.get('/load-new', { params })
    return response.data?.data?.newloads || []
  },

  /**
   * Search for a single load using searchQuery
   * Uses the same searchQuery pattern as the main B18 app listLoads
   * Searches: load number, customer load number, customer/broker name
   */
  searchByQuery: async (query: string) => {
    const params = new URLSearchParams()
    params.append('conditions[searchQuery]', query.trim())
    params.append('options[limit]', '1')
    params.append('options[sort][loadNumber]', 'desc')

    const response = await apiClient.get('/load-new', { params })
    return response.data?.data?.newloads?.[0] || null
  },

  /**
   * Get full load details by ID
   */
  getLoadById: async (loadId: string) => {
    const params = new URLSearchParams()
    params.append('loadid', loadId)

    const response = await apiClient.get('/load-detail-by-id', { params })
    return response.data?.data || null
  },

  /**
   * Get load by load number or search query (combines search + details)
   * Searches: load number, customer load number, customer/broker name
   * Uses searchQuery just like the main B18 app
   */
  getLoadByNumber: async (loadNumber: string) => {
    // Use searchQuery to find the load (searches across load number, customer load number, customer/broker name)
    const searchResult = await loadApi.searchByQuery(loadNumber)
    if (!searchResult?._id) return null
    return loadApi.getLoadById(searchResult._id)
  }
}

interface FrontLinkWithLoad {
  loadId: string
  loadNumber?: unknown
}

const hasLoadId = (link: unknown): link is FrontLinkWithLoad =>
  typeof link === 'object' &&
  link !== null &&
  'loadId' in link &&
  typeof link.loadId === 'string' &&
  link.loadId !== ''

/**
 * Front integration API functions
 */
export const frontApi = {
  /**
   * Get load linked to a conversation (linked via B18 app)
   * Returns the newest link's load ID if the conversation is linked, null otherwise
   */
  getLinkedLoad: async (
    conversationId: string
  ): Promise<{ loadId: string; loadNumber?: string } | null> => {
    try {
      const response = await apiClient.get('/front/links', {
        params: { conversationId }
      })
      const links: unknown = response.data?.data?.links
      if (!Array.isArray(links)) return null
      // The backend sorts links newest first, so the first usable one is the current link
      const link = links.find(hasLoadId)
      if (!link) return null
      const { loadNumber } = link
      if (typeof loadNumber === 'string') {
        return { loadId: link.loadId, loadNumber }
      }
      if (typeof loadNumber === 'number') {
        return { loadId: link.loadId, loadNumber: String(loadNumber) }
      }
      return { loadId: link.loadId }
    } catch (_error) {
      // A backend without the conversationId filter answers 400, and an unlinked conversation is not an error
      return null
    }
  }
}

/**
 * User API functions
 */
export const userApi = {
  /**
   * Login to B18 and get user profile with permissions
   * This is called after Firebase authentication to get B18 user data
   */
  login: async (email: string, uid: string): Promise<B18User | null> => {
    try {
      const response = await apiClient.post('/login', {
        email,
        uid,
        ipAddress: 'front-plugin',
        userAgent: navigator.userAgent
      })
      return response.data?.data || null
    } catch (error) {
      logger.error('Failed to login to B18', error as Error, {
        component: 'api',
        action: 'login'
      })
      return null
    }
  }
}

/**
 * One rule from `role.permissions_rules`: every action a role holds on one subject, e.g.
 * `{ action: ['read', 'stops.read'], subject: 'load' }`. Sent by the backend on login next to the
 * old `permissions` matrix.
 */
export interface PermissionRule {
  action: string | string[]
  subject: string
  inverted?: boolean
}

/**
 * B18 User type with role and permissions
 */
export interface B18User {
  _id: string
  email: string
  name?: string
  firstName?: string
  lastName?: string
  role?: {
    _id: string
    name: string
    permissions?: {
      load?: {
        read?: boolean
        create?: boolean
        update?: boolean
        delete?: boolean
        stops?: boolean
        dispatch?: boolean
        document?: boolean
        invoice?: boolean
      }
      [key: string]: unknown
    }
    /** The same grants as `permissions`, as named rules. Absent for a session from before the backend change. */
    permissions_rules?: PermissionRule[]
  }
}

export default apiClient
